/**
 * Configuration centralisée du logo selon la langue, le thème, la taille d'écran et l'emplacement
 */

export type LogoLocation = 'navbar' | 'footer' | 'splash' | 'login';
export type LogoLanguage = 'fr' | 'en' | 'ar';
export type LogoTheme = 'light' | 'dark';
export type LogoSize = 'mobile' | 'desktop';

export interface LogoConfig {
  // Le texte principal à afficher
  primaryText: string;
  // Le texte secondaire (pour le splash screen)
  secondaryText?: string;
  // La lettre principale (toujours bleue)
  primaryLetter: string;
  // Le reste du texte (blanc/noir selon le thème)
  restText: string;
  // Si c'est un mini-logo
  isMini: boolean;
  // Si c'est pour le splash screen (affiche deux logos)
  isSplash: boolean;
  // Direction RTL pour l'arabe
  isRTL: boolean;
}

/**
 * Obtient la configuration du logo selon les paramètres fournis
 */
export function getLogoConfig(
  language: LogoLanguage,
  theme: LogoTheme,
  size: LogoSize,
  location: LogoLocation
): LogoConfig {
  const isArabic = language === 'ar';
  const isSplash = location === 'splash';
  
  // Déterminer si on affiche le mini-logo ou le logo complet
  // Le splash et le login utilisent le mini-logo comme logo principal
  const useMiniLogo = isSplash || location === 'login';
  
  if (isArabic) {
    // Configuration pour l'arabe
    if (useMiniLogo) {
      return {
        primaryText: '.ء',
        secondaryText: isSplash ? 'ء خدمات' : undefined,
        primaryLetter: 'ء',
        restText: '.',
        isMini: true,
        isSplash,
        isRTL: true,
      };
    } else {
      return {
        primaryText: 'ء خدمات',
        primaryLetter: 'ء',
        restText: ' خدمات',
        isMini: false,
        isSplash,
        isRTL: true,
      };
    }
  } else {
    // Configuration pour français/anglais
    if (useMiniLogo) {
      return {
        primaryText: 'e.',
        secondaryText: isSplash ? 'eServices' : undefined,
        primaryLetter: 'e',
        restText: '.',
        isMini: true,
        isSplash,
        isRTL: false,
      };
    } else {
      return {
        primaryText: 'eServices',
        primaryLetter: 'e',
        restText: 'Services',
        isMini: false,
        isSplash,
        isRTL: false,
      };
    }
  }
}

/**
 * Obtient la couleur du texte selon le thème
 * La lettre principale est toujours bleue, le reste est blanc/noir selon le thème
 */
export function getTextColor(theme: LogoTheme, isPrimaryLetter: boolean): string {
  if (isPrimaryLetter) {
    return 'text-primary-600 dark:text-primary-500';
  }
  // Le reste du texte est blanc en dark mode et noir en light mode
  return 'text-gray-900 dark:text-white';
}
